import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import {
  X, ArrowLeft, Plus, Send, Loader2, MessageSquare, Sparkles, Trash2,
  AlertCircle, Lock, Swords,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  useChatConversations, useChatConversation, useCreateChatConversation,
  useSendChatMessage, useArchiveChatConversation,
  useStartGodMode, useEndGodMode,
  type ChatMessage, type GodModeState,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

function relativeTime(iso: string): string {
  const dt = (Date.now() - new Date(iso).getTime()) / 1000;
  if (dt < 60)        return "just now";
  if (dt < 3600)      return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86_400)    return `${Math.floor(dt / 3600)}h ago`;
  return `${Math.floor(dt / 86_400)}d ago`;
}

function MessageBubble({ m }: { m: ChatMessage }) {
  const isUser = m.role === "user";
  const isRitual = m.meta?.kind === "ritual";

  // Ritual lines (the Diablog) are styled distinctively — gold left
  // border, ⚔ glyph, italic text. They sit centred to read as a beat
  // in the conversation rather than a normal Tyrael reply.
  if (isRitual) {
    return (
      <div className="flex w-full justify-center">
        <div className="flex max-w-[92%] items-start gap-2 border-l-2 border-amber-500/70 bg-amber-500/5 px-3 py-2 italic text-amber-900 dark:text-amber-200">
          <Swords className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="whitespace-pre-wrap text-sm leading-snug">{m.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[88%] rounded-lg px-3 py-2 text-sm leading-snug",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{m.content}</div>
        ) : (
          <div className={cn(
            "prose prose-sm max-w-none",
            // Base text + structural elements
            "text-foreground prose-headings:text-foreground prose-strong:text-foreground",
            "prose-a:text-primary prose-li:text-foreground prose-p:text-foreground",
            // Inline code: theme-aware bg + foreground text. Strip the
            // `before/after` content (typography plugin wraps inline
            // code in backticks by default, which doubles up with the
            // monospace styling and looks ugly).
            "prose-code:bg-background prose-code:text-foreground",
            "prose-code:rounded prose-code:px-1 prose-code:py-0.5",
            "prose-code:border prose-code:border-border",
            "prose-code:before:hidden prose-code:after:hidden",
            "prose-code:font-normal",
            // Code blocks (```): override the typography defaults so
            // the bg + text actually have contrast in both light and
            // dark themes. Default prose ships with a near-black bg
            // and dim text that's unreadable against the muted bubble.
            "prose-pre:bg-background prose-pre:text-foreground",
            "prose-pre:border prose-pre:border-border",
            "prose-pre:rounded-md",
            // Dark-mode hook for the rest (links, etc.)
            "dark:prose-invert dark:prose-pre:bg-muted dark:prose-pre:text-foreground",
            "dark:prose-code:bg-muted dark:prose-code:text-foreground",
          )}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
          </div>
        )}
        {!isUser && m.duration_s !== null && (
          <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
            <span>{m.duration_s}s</span>
            {m.ok === false && (
              <span className="inline-flex items-center gap-0.5 text-red-600 dark:text-red-400">
                <AlertCircle className="h-3 w-3" />
                {m.error || "failed"}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationList({
  onPick, onNew,
}: {
  onPick: (id: number) => void;
  onNew: () => void;
}) {
  const list = useChatConversations();
  const archive = useArchiveChatConversation();

  const items = list.data?.items ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border p-3">
        <button
          type="button"
          onClick={onNew}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> New conversation
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {list.isLoading && (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        )}
        {!list.isLoading && items.length === 0 && (
          <div className="p-6 text-center">
            <MessageSquare className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-xs text-muted-foreground">
              No conversations yet. Start one above.
            </p>
          </div>
        )}
        {items.map((c) => (
          <div
            key={c.id}
            className="group flex items-start gap-2 border-b border-border px-3 py-2 hover:bg-accent/40"
          >
            <button
              type="button"
              onClick={() => onPick(c.id)}
              className="min-w-0 flex-1 text-left"
            >
              <div className="truncate text-sm font-medium">{c.title}</div>
              <div className="text-[11px] text-muted-foreground">
                {relativeTime(c.last_message_at)}
              </div>
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!confirm("Archive this conversation?")) return;
                await archive.mutateAsync(c.id);
              }}
              title="Archive"
              className="rounded-md p-1 text-muted-foreground opacity-0 hover:bg-accent group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GodModeBadge({
  state, expiresAt, onEnd,
}: {
  state: GodModeState;
  expiresAt: string | null | undefined;
  onEnd: () => void;
}) {
  // Live tick so the displayed countdown updates every second without
  // re-fetching from the server. The state itself only changes server-
  // side; here we just paint the remaining time.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (state !== "on" || !expiresAt) return;
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [state, expiresAt]);

  const remaining = useMemo(() => {
    if (state !== "on" || !expiresAt) return 0;
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.floor(ms / 1000));
  }, [state, expiresAt, tick]);

  if (state === "on") {
    const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
    const ss = String(remaining % 60).padStart(2, "0");
    return (
      <button
        type="button"
        onClick={onEnd}
        title="Click to revoke god mode early"
        className="inline-flex items-center gap-1 rounded-md border border-red-500/60 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700 dark:text-red-400"
      >
        <Swords className="h-3 w-3" />
        GOD MODE · {mm}:{ss}
      </button>
    );
  }

  if (state === "awaiting_q1" || state === "awaiting_q2") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700 dark:text-amber-400">
        <Swords className="h-3 w-3 animate-pulse" />
        Audience…
      </span>
    );
  }

  if (state === "locked_out") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        <Lock className="h-3 w-3" />
        Sealed
      </span>
    );
  }

  return null;
}

function ConversationView({
  conversationId, onBack,
}: {
  conversationId: number;
  onBack: () => void;
}) {
  const conv = useChatConversation(conversationId);
  const send = useSendChatMessage();
  const startGodMode = useStartGodMode();
  const endGodMode = useEndGodMode();

  const [input, setInput] = useState("");
  const [pendingUserMsg, setPendingUserMsg] = useState<string | null>(null);
  // Optimistic assistant placeholder — replaced when the assistant_message
  // event arrives. While the stream is open we update `thinkingElapsed`
  // so the user sees a live elapsed-time counter.
  const [thinkingElapsed, setThinkingElapsed] = useState<number | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  // Optimistic god-mode state during a streaming exchange — keeps the
  // header badge in sync before the conversation refetch lands.
  const [optimisticGmState, setOptimisticGmState] = useState<GodModeState | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const gmState: GodModeState = optimisticGmState ?? conv.data?.god_mode_state ?? "off";
  const gmExpires = conv.data?.god_mode_expires_at ?? null;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conv.data?.messages.length, pendingUserMsg, thinkingElapsed]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || send.isPending) return;
    setInput("");
    setPendingUserMsg(content);
    setThinkingElapsed(0);
    setStreamError(null);
    try {
      await send.mutateAsync({
        conversationId,
        content,
        onEvent: (ev) => {
          if (ev.type === "user_message") {
            // The persisted user message is now in the DB; clear the
            // optimistic bubble — the next conv refetch will render it.
            setPendingUserMsg(null);
          } else if (ev.type === "thinking") {
            setThinkingElapsed(ev.elapsed_s);
          } else if (ev.type === "assistant_message") {
            setThinkingElapsed(null);
          } else if (ev.type === "god_mode_state") {
            setOptimisticGmState(ev.state);
          } else if (ev.type === "error") {
            setStreamError(ev.error);
            setThinkingElapsed(null);
          }
        },
      });
    } catch (err) {
      setStreamError(err instanceof Error ? err.message : "Send failed");
    } finally {
      setPendingUserMsg(null);
      setThinkingElapsed(null);
      // Clear optimistic GM state after a tick — the refetch will have
      // landed by then with the authoritative value.
      setTimeout(() => setOptimisticGmState(null), 100);
    }
  };

  const onStartGodMode = async () => {
    if (startGodMode.isPending) return;
    try {
      const res = await startGodMode.mutateAsync(conversationId);
      setOptimisticGmState(res.new_state);
    } catch (err) {
      setStreamError(err instanceof Error ? err.message : "Couldn't start god mode");
    }
  };

  const onEndGodMode = async () => {
    if (endGodMode.isPending) return;
    if (!confirm("Revoke god mode now?")) return;
    try {
      const res = await endGodMode.mutateAsync(conversationId);
      setOptimisticGmState(res.new_state);
    } catch (err) {
      setStreamError(err instanceof Error ? err.message : "Couldn't revoke");
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e as unknown as FormEvent);
    }
  };

  const messages = conv.data?.messages ?? [];

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        // When god mode is active, the entire conversation gets a 1px
        // red ring so the operator literally sees they're holding a
        // live wire.
        gmState === "on" && "ring-2 ring-red-500/70 ring-inset",
      )}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          title="Back to conversations"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {conv.data?.title ?? "…"}
          </div>
        </div>
        <GodModeBadge
          state={gmState}
          expiresAt={gmExpires}
          onEnd={onEndGodMode}
        />
        {gmState === "off" && (
          <button
            type="button"
            onClick={onStartGodMode}
            disabled={startGodMode.isPending}
            title="Begin the audience (god mode)"
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            {startGodMode.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Lock className="h-4 w-4" />}
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3">
        {conv.isLoading && (
          <div className="text-sm text-muted-foreground">Loading…</div>
        )}
        {messages.map((m) => <MessageBubble key={m.id} m={m} />)}
        {pendingUserMsg && (
          <div className="flex w-full justify-end">
            <div className="max-w-[88%] rounded-lg bg-primary/70 px-3 py-2 text-sm text-primary-foreground">
              <div className="whitespace-pre-wrap">{pendingUserMsg}</div>
            </div>
          </div>
        )}
        {thinkingElapsed !== null && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Tyrael is thinking… {thinkingElapsed > 0 && <span>{Math.round(thinkingElapsed)}s</span>}
          </div>
        )}
        {streamError && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
            {streamError}
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={send.isPending}
            placeholder="Ask Tyrael… (Enter to send, Shift+Enter for newline)"
            rows={2}
            className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={send.isPending || !input.trim()}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            title="Send"
          >
            {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ChatPanel({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [activeId, setActiveId] = useState<number | null>(null);
  const create = useCreateChatConversation();

  // Reset active conversation when the panel re-opens — feels right
  // to land on the conversation list, not on whatever the user was
  // last reading. (If we wanted "resume last", we'd persist activeId
  // in localStorage; not worth it for v1.)
  useEffect(() => {
    if (!open) setActiveId(null);
  }, [open]);

  const onNew = async () => {
    const r = await create.mutateAsync({});
    setActiveId(r.id);
  };

  return (
    <>
      {/* Backdrop. Click-through to close on desktop too. */}
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/30 transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      {/* Drawer. Right-anchored, full height, ~440px on md+. */}
      <aside
        className={cn(
          "fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-card shadow-2xl transition-transform",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="flex-1 text-sm font-semibold">Tyrael</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          {activeId === null ? (
            <ConversationList
              onPick={(id) => setActiveId(id)}
              onNew={onNew}
            />
          ) : (
            <ConversationView
              conversationId={activeId}
              onBack={() => setActiveId(null)}
            />
          )}
        </div>
      </aside>
    </>
  );
}
